import { format } from 'date-fns';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { showToast } from '../components/reusables/CommonFunction';

export const isCurrentMonth = (date, currentDate) => {
    return format(date, 'M') === format(currentDate, 'M');
};

export const generateAppointmentId = () => {
    return Math.random().toString(36).substr(2, 9);
};

export const formatDisplayDate = (dateString) => {
    if (dateString === "" || dateString === null) {
        return "";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export const formatDisplayTime = (timeString) => {
    /* Converts a time string in the format "HH:mm" to "hh:mm AM/PM"
       Example: "14:30" becomes "02:30 PM" */

    if (!timeString || typeof timeString !== 'string' || !timeString.includes(':')) return '';

    const [hourStr, minute] = timeString.split(':');
    let hour = parseInt(hourStr, 10);

    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 === 0 ? 12 : hour % 12;

    return `${String(hour).padStart(2, '0')}:${minute} ${ampm}`;
}

/**
 * Professional Excel Export Utility
 * ---------------------------------
 * - Auto adjusts column widths
 * - Adds styled header row
 * - Freezes header row for easier scrolling
 * - Formats currency & dates intelligently
 * - Includes metadata section (e.g. filters, report info)
 *
 * @param {Array} data - Array of JSON objects to export
 * @param {String} fileName - The name of the Excel file (without extension)
 * @param {Array} columns - Array of column definitions for headers (e.g., [{ label: "Patient Name", key: "patient_name" }])
 * @param {Object} metadata - Optional key/value filters displayed below title
 * @param {String} title - Optional report title (e.g., "Patient Invoices Report")
 * @param {String} clinicName - Optional clinic name
 */

export const exportToExcel = (data, fileName, columns, metadata = {}, title = "Data Report", clinicName = "") => {
    if (!data || data.length === 0) {
        showToast("No data available to export.");
        return;
    }

    // Step 1: Title rows (Clinic name + Report title)
    const titleRows = [
        [`${clinicName}`],
        [`${title}`],
        [""], // blank line
    ];

    // Step 2: Metadata rows
    const metaRows = [];
    if (Object.keys(metadata).length > 0) {
        metaRows.push(["Report Filters:"]);
        Object.entries(metadata).forEach(([key, value]) => {
            metaRows.push([`${key}`, value || "-"]);
        });
        metaRows.push([]);
    }

    // Step 3: Format data intelligently
    const exportData = data.map((item) => {
        const row = {};
        columns.forEach((col) => {
            let value = item[col.key];
            if (value === null || value === undefined) value = "";

            const label = col.label.toLowerCase();
            if (label.includes("date") && value) {
                value = formatDisplayDate(value);
            } else if (label.includes("time") && value) {
                value = formatDisplayTime(value);
            } else if (
                label.includes("amount") ||
                label.includes("total") ||
                label.includes("paid") ||
                label.includes("balance") ||
                label.includes("fee")
            ) {
                value = `₹${Number(value || 0).toFixed(2)}`;
            }

            row[col.label] = value;
        });
        return row;
    });

    // Step 4: Combine all rows
    const worksheet = XLSX.utils.json_to_sheet(exportData, { origin: titleRows.length + metaRows.length });
    XLSX.utils.sheet_add_aoa(worksheet, [...titleRows, ...metaRows], { origin: "A1" });

    // Step 5: Auto column width
    const allData = [...titleRows, ...metaRows, columns.map((c) => c.label), ...exportData.map(Object.values)];
    const colWidths = columns.map((col, i) => {
        const maxLen = allData.reduce((max, row) => {
            const cell = row[i] ? row[i].toString() : "";
            return Math.max(max, cell.length);
        }, col.label.length);
        return { wch: Math.min(maxLen + 5, 40) };
    });
    worksheet["!cols"] = colWidths;


    // Freeze header row (keep column titles visible while scrolling)
    const headerRowIndex = titleRows.length + metaRows.length;
    worksheet["!freeze"] = { xSplit: 0, ySplit: headerRowIndex + 1 };

    // Step 6: Styling setup
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    const clinicRowIndex = 0;
    const titleRowIndex = 1;

    for (let R = 0; R <= range.e.r; R++) {
        for (let C = 0; C <= range.e.c; C++) {
            const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = worksheet[cellRef];
            if (!cell) continue;

            // Clinic name row
            if (R === clinicRowIndex) {
                cell.s = {
                    font: { bold: true, sz: 14, color: { rgb: "1F4E78" }, name: "Calibri" },
                    alignment: { horizontal: "center", vertical: "center" },
                };
            }

            // Report title row
            else if (R === titleRowIndex) {
                cell.s = {
                    font: { bold: true, sz: 13, color: { rgb: "2F5496" }, name: "Calibri" },
                    alignment: { horizontal: "center", vertical: "center" },
                };
            }

            // sHeader styling
            else if (R === headerRowIndex) {
                cell.s = {
                    font: { bold: true, color: { rgb: "FFFFFF" }, name: "Calibri", sz: 12 },
                    fill: { fgColor: { rgb: "305496" } },
                    alignment: { horizontal: "center", vertical: "center" },
                    border: {
                        top: { style: "thin", color: { rgb: "DDDDDD" } },
                        bottom: { style: "thin", color: { rgb: "DDDDDD" } },
                    },
                };
            }

            // Normal data cells
            else {
                cell.s = {
                    font: { name: "Calibri", sz: 9.5 },
                    alignment: { horizontal: "left", vertical: "center" },
                };
            }
        }
    }

    // Step 7: Merge title cells across columns
    const mergeCols = columns.length - 1;
    worksheet["!merges"] = [
        { s: { r: clinicRowIndex, c: 0 }, e: { r: clinicRowIndex, c: mergeCols } },
        { s: { r: titleRowIndex, c: 0 }, e: { r: titleRowIndex, c: mergeCols } },
    ];

    // Step 8: Workbook and export
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "Data");

    const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
    });

    const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `${fileName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
};
